import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
  showItemsInfo?: boolean;
  className?: string;
  previousLabel?: string;
  nextLabel?: string;
  renderItemsInfo?: (info: {
    startItem: number;
    endItem: number;
    totalItems: number;
  }) => React.ReactNode;
}

const MOBILE_MAX_VISIBLE_PAGES = 1;

export const PaginationComponent: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  maxVisiblePages = 5,
  showItemsInfo = true,
  className,
  previousLabel = "Previous",
  nextLabel = "Next",
  renderItemsInfo,
}) => {
  const isMobile = useIsMobile();

  if (totalPages <= 1) {
    return null;
  }

  const visiblePagesLimit = isMobile ? MOBILE_MAX_VISIBLE_PAGES : maxVisiblePages;

  const getVisiblePages = (): number[] => {
    if (totalPages <= visiblePagesLimit) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(visiblePagesLimit / 2);
    let start = Math.max(1, currentPage - half);
    const end = Math.min(totalPages, start + visiblePagesLimit - 1);

    if (end - start + 1 < visiblePagesLimit) {
      start = Math.max(1, end - visiblePagesLimit + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();
  const showStartEllipsis = visiblePages[0] > 1;
  const showEndEllipsis = visiblePages[visiblePages.length - 1] < totalPages;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div className={`flex flex-col items-center justify-between gap-4 ${className || ''}`}>
      <Pagination className="mx-0">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={handlePrevious}
              size={isMobile ? "icon" : "default"}
              className={cn(
                isMobile && "px-0",
                currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
              )}
            >
              <ChevronLeft className="h-4 w-4" />
              {!isMobile && <span>{previousLabel}</span>}
            </PaginationPrevious>
          </PaginationItem>
          {showStartEllipsis && (
            <>
              <PaginationItem>
                <PaginationLink
                  onClick={() => handlePageClick(1)}
                  isActive={currentPage === 1}
                  className="cursor-pointer"
                >
                  1
                </PaginationLink>
              </PaginationItem>
              {visiblePages[0] > 2 && (
                <PaginationItem>
                  <PaginationEllipsis className={cn(isMobile && "w-6")} />
                </PaginationItem>
              )}
            </>
          )}
          {visiblePages.map((page) => (
            <PaginationItem key={page}>
              <PaginationLink
                onClick={() => handlePageClick(page)}
                isActive={currentPage === page}
                className="cursor-pointer"
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ))}
          {showEndEllipsis && (
            <>
              {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                <PaginationItem>
                  <PaginationEllipsis className={cn(isMobile && "w-6")} />
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink
                  onClick={() => handlePageClick(totalPages)}
                  isActive={currentPage === totalPages}
                  className="cursor-pointer"
                >
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
              
            </>
          )}
          <PaginationItem>
            <PaginationNext
              onClick={handleNext}
              size={isMobile ? "icon" : "default"}
              className={cn(
                isMobile && "px-0",
                currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
              )}
            >
              {!isMobile && <span>{nextLabel}</span>}
              <ChevronRight className="h-4 w-4" />
            </PaginationNext>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      {showItemsInfo && (
        <div className="text-sm text-muted-foreground">
          {renderItemsInfo ? (
            renderItemsInfo({ startItem, endItem, totalItems })
          ) : (
            <>
              Showing {startItem} to {endItem} of {totalItems} items
            </>
          )}
        </div>
      )}
    </div>
  );
};
export default PaginationComponent;
